/**
 * The patient ledger's words and colours, ported from MedicAI Connect's patient ledger so both read the same.
 */
import { formatCents, Tone } from './connect-dashboard.util';

export interface LedgerLine {
  id: string;
  tariffCode: string;
  description: string | null;
  billedCents: number;
  approvedCents: number | null;
  rejectedCents: number | null;
  patientLiableCents: number | null;
  paidCents: number;
  status: string | null;
  reasonCodes: Array<{ code: string; type?: string; description?: string }> | null;
}

export interface LedgerSettlement {
  status: 'settled' | 'part_settled' | 'outstanding' | 'written_off' | 'reversed' | 'unbilled';
  outstandingCents: number;
  patientOutstandingCents: number;
  schemeOutstandingCents: number;
}

export interface LedgerDocument {
  id: string;
  type: string;
  status: string;
  claimStatus: string | null;
  serviceDate: string | null;
  createdAt: string;
  billedCents: number;
  trackingId: string | null;
  hnetNumber: string | null;
  lines: LedgerLine[];
  settlement: LedgerSettlement;
}

export interface LedgerActivityEvent {
  entryId: string;
  timestamp: string;
  sourceType: string;
  label: string;
  documentId: string | null;
  schemeMovementCents: number;
  patientMovementCents: number;
  schemeBalanceCents: number;
  patientBalanceCents: number;
  netCents: number | null;
  vatCents: number | null;
  writtenOff?: boolean;
}

export interface PatientLedger {
  patientName: string;
  header: {
    schemeOwedCents: number;
    patientOwedCents: number;
    aging: { current: number; d30: number; d60: number; d90: number; d120plus: number; creditCents: number };
  };
  documents: LedgerDocument[];
  activity: LedgerActivityEvent[];
}

export interface LedgerPosting {
  id: string;
  account: { name: string };
  debitCents: number;
  creditCents: number;
}

export interface LedgerLineTransaction extends LedgerPosting {
  timestamp: string;
  label: string;
}

/** Closed documents: they carry no money. */
const DOC_TERMINAL_STATUSES = ['superseded', 'void', 'written_off'];

/** A live, sent claim shows what the scheme said; a draft or a closed document shows its own status. */
export function docStatus(doc: Pick<LedgerDocument, 'type' | 'status' | 'claimStatus'>): string {
  if (doc.type === 'claim' && !DOC_TERMINAL_STATUSES.includes(doc.status)) {
    if (doc.status !== 'draft' && doc.claimStatus) return doc.claimStatus;
  }
  return doc.status;
}

/** A document's money in one line: billed, paid, and who still owes what. */
export function documentStory(doc: Pick<LedgerDocument, 'billedCents' | 'lines' | 'settlement'>): { label: string; cents: number }[] {
  const paid = doc.lines.reduce((s, l) => s + (Number(l.paidCents) || 0), 0);
  const parts = [
    { label: 'Billed', cents: doc.billedCents },
    { label: 'Paid', cents: paid },
    { label: 'Scheme owes', cents: doc.settlement.schemeOutstandingCents },
    { label: 'Patient owes', cents: doc.settlement.patientOutstandingCents },
  ];
  return parts.filter((p, i) => i === 0 || p.cents !== 0);
}

/** "credit_note" as "Credit note". */
export function documentKind(type: string): string {
  const t = type.replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

const STATUS_TONES: Record<string, Tone> = {
  approved: 'success', paid: 'success', accepted: 'success',
  rejected: 'danger', failed: 'danger', reversed: 'danger', void: 'danger', written_off: 'danger', declined: 'danger',
  submitted: 'info', ready: 'info', processing: 'info', sent: 'info', issued: 'info',
  partial: 'warning', partially_paid: 'warning',
};

/** A reversed settlement stays neutral: nothing is due, and the status beside it already says reversed. */
const SETTLEMENT_TONES: Record<string, Tone> = {
  settled: 'success', part_settled: 'warning', outstanding: 'info', written_off: 'danger',
};

export function statusTone(status: string | null | undefined): Tone {
  return STATUS_TONES[status ?? ''] ?? 'neutral';
}

export function settlementTone(status: string | null | undefined): Tone {
  return SETTLEMENT_TONES[status ?? ''] ?? 'neutral';
}

export function settlementLabel(status: string): string {
  const labels: Record<string, string> = {
    settled: 'Settled', reversed: 'Reversed', written_off: 'Written off', part_settled: 'Part paid', unbilled: 'Not billed',
  };
  return labels[status] ?? 'Outstanding';
}

export type LedgerGroup = 'claims' | 'scheme_payments' | 'patient_payments' | 'adjustments';

const GROUPS: Record<LedgerGroup, string[]> = {
  claims: [
    'billing_document_issue', 'claim_submit', 'claim_charge', 'claim', 'claim_resubmission', 'claim_amendment',
    'claim_adjudication', 'adjudication', 'era_adjudication', 'era_resolve_adjudication', 'era_recovery_adjudication',
    'paper_claim_shortfall', 'claim_reversal', 'claim_superseded', 'claim_void', 'reversal',
  ],
  scheme_payments: [
    'era_cash', 'era_resolve', 'scheme_payment', 'manual_scheme_payment', 'era_unapplied', 'era_recovery',
    'era_scheme_adjustment', 'era_carry_forward',
  ],
  patient_payments: [
    'patient_payment', 'payment', 'patient_refund', 'allocate_credit', 'patient_credit_allocation', 'credit_allocation',
    'billing_document_void_credit', 'void_credit',
  ],
  adjustments: [],
};

/** The kind of a history entry, for its pill colour. */
export function ledgerGroup(sourceType: string): LedgerGroup {
  return (Object.keys(GROUPS) as LedgerGroup[]).find((g) => GROUPS[g].includes(sourceType)) ?? 'adjustments';
}

/** What an entry changed about who owes what (positive = owes more). */
export function ledgerEffectText(schemeCents: number, patientCents: number, writtenOff = false): string {
  if (schemeCents === 0 && patientCents === 0) return 'No change to what is owed';
  if (writtenOff && schemeCents <= 0 && patientCents <= 0) {
    const whose = schemeCents === 0 ? ' what the patient owes' : patientCents === 0 ? ' what the scheme owes' : '';
    return `${formatCents(-(schemeCents + patientCents))} written off${whose}`;
  }
  if (schemeCents !== 0 && schemeCents === -patientCents) {
    return schemeCents < 0
      ? `${formatCents(patientCents)} moved from the scheme to the patient`
      : `${formatCents(schemeCents)} moved from the patient to the scheme`;
  }
  const part = (who: string, cents: number) => `${who} owes ${formatCents(Math.abs(cents))} ${cents > 0 ? 'more' : 'less'}`;
  return [schemeCents !== 0 ? part('Scheme', schemeCents) : null, patientCents !== 0 ? part('Patient', patientCents) : null]
    .filter(Boolean)
    .join(' · ');
}
